import { AppUser } from "features/user/types";
import { BaseColorKey } from "features/user/constants";

/**
 * ユーザータイプ（子供ユーザー追加専用）
 * - GUEST: 体験ユーザー
 * - CHILD: 子供ユーザー
 */
export enum UserType {
  GUEST = "guest",
  CHILD = "child",
}

/**
 * ウィザードのステップ
 */
export enum WizardStep {
  NAME_INPUT = "name",
  ICON_METHOD = "icon_method",
  PRESET_ICON = "preset_icon",
  UPLOAD_ICON = "upload_icon",
  PROFILE_AI = "profile_ai",
  PREVIEW = "preview",
}

/**
 * アイコン選択方法
 */
export enum IconMethod {
  PRESET = "preset",
  UPLOAD = "upload",
  PROFILE_AI = "profile_ai",
}

/**
 * プロフィール入力のサブステップ
 */
export enum ProfileSubStep {
  GENDER = "gender",
  BIRTHDAY = "birthday",
  FAVORITES = "favorites",
  ICON_PREVIEW = "icon_preview",
}

/**
 * 好きなもの選択のサブステップ（子供向け）
 */
export enum FavoritesSubStep {
  APPEARANCE = "appearance",
  COLOR = "color",
  BEST_SUBJECT = "bestSubject",
}

/**
 * サブステップ定義（旧版 - 後方互換用）
 */
export interface SubStepDefinition {
  key: ProfileSubStep;
  labelKey: string;
  number: number;
}

/**
 * 全ステップ定義（フラット）
 */
export interface AllStepDefinition {
  id: string;
  profileSubStep: ProfileSubStep;
  favoritesSubStep?: FavoritesSubStep;
  labelKey: string;
  number: number;
}

/**
 * 全ステップ一覧（6ステップ）
 * 順序: 性別 → 誕生日 → みため → 好きないろ → 得意な教科 → アイコン
 */
export const ALL_PROFILE_STEPS: AllStepDefinition[] = [
  {
    id: "gender",
    profileSubStep: ProfileSubStep.GENDER,
    labelKey: "MSG_PROFILE_SUBSTEP_GENDER",
    number: 1,
  },
  {
    id: "birthday",
    profileSubStep: ProfileSubStep.BIRTHDAY,
    labelKey: "MSG_PROFILE_SUBSTEP_BIRTHDAY",
    number: 2,
  },
  {
    id: "appearance",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.APPEARANCE,
    labelKey: "MSG_PROFILE_AI_APPEARANCE",
    number: 3,
  },
  {
    id: "color",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.COLOR,
    labelKey: "MSG_PROFILE_AI_FAVORITE_COLOR",
    number: 4,
  },
  {
    id: "bestSubject",
    profileSubStep: ProfileSubStep.FAVORITES,
    favoritesSubStep: FavoritesSubStep.BEST_SUBJECT,
    labelKey: "MSG_PROFILE_AI_BEST_SUBJECT",
    number: 5,
  },
  {
    id: "icon",
    profileSubStep: ProfileSubStep.ICON_PREVIEW,
    labelKey: "MSG_PROFILE_SUBSTEP_ICON",
    number: 6,
  },
];

/**
 * 性別（3択）
 */
export enum ChildGender {
  MALE = "male",
  FEMALE = "female",
  NONE = "none",
}

/**
 * 容姿の型定義
 */
export type AccessoryType = "none" | "megane" | "boushi" | "hairpin" | "ribbon" | "bandaid";
export type HairstyleType = "very_short" | "short" | "medium" | "slightly_long" | "long";
export type BodyTypeType = "thin" | "slightly_thin" | "medium" | "slightly_chubby" | "chubby";
export type FaceShapeType = "round" | "oval" | "oblong" | "square" | "triangle";

/**
 * アップロードされたアイコンのメタデータ
 */
export interface UploadedIconMetadata {
  source: "uploaded";
  filename: string;
  content_type: string;
  file_size: number;
  width: string;
  height: string;
}

/**
 * ウィザードの状態
 */
export interface CreateUserState {
  // 基本情報
  name: string;
  icon: string;
  iconMetadata?: UploadedIconMetadata;
  iconMethod: IconMethod | null;

  // プロフィール情報
  gender: ChildGender | null;
  birthday: string;
  birthdayYear: number | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;

  // 好きないろ（2段階選択）
  // Step 1: ベース色選択（複数選択可）
  selectedBaseColors: BaseColorKey[];
  // Step 2: 各ベース色のバリエーション選択後の最終結果（"red-3" 形式で保存）
  favoriteColors: string[];

  // 得意な教科（複数選択）
  bestSubjects: string[];

  // 容姿
  accessory: AccessoryType[];
  hairstyle: HairstyleType | null;
  bodyType: BodyTypeType | null;
  faceShape: FaceShapeType | null;
}

/**
 * ステップ定義
 */
export interface StepDefinition {
  key: WizardStep;
  labelKey: string;
  icon: string;
}

/**
 * CreateUserWizard のプロパティ
 */
export interface CreateUserWizardProps {
  open: boolean;
  close: () => void;
  userType: UserType;
  onSuccess?: (user: AppUser) => void;
}

/**
 * ステップ定義（常に3ステップ）
 * プリセット・プロフィールAI両方で使用
 */
export const WIZARD_STEPS: StepDefinition[] = [
  {
    key: WizardStep.NAME_INPUT,
    labelKey: "MSG_CREATE_USER_STEP_NAME",
    icon: "edit",
  },
  {
    key: WizardStep.ICON_METHOD,
    labelKey: "MSG_CREATE_USER_STEP_ICON",
    icon: "palette",
  },
  {
    key: WizardStep.PREVIEW,
    labelKey: "MSG_CREATE_USER_STEP_CONFIRM",
    icon: "check_circle",
  },
];

/**
 * 初期状態
 */
export const initialState: CreateUserState = {
  name: "",
  icon: "",
  iconMethod: null,
  gender: null,
  birthday: "",
  birthdayYear: null,
  birthdayMonth: null,
  birthdayDay: null,
  selectedBaseColors: [],
  favoriteColors: [],
  bestSubjects: [],
  accessory: [],
  hairstyle: null,
  bodyType: null,
  faceShape: null,
};
